import { useCallback, useMemo } from 'react';

import { Diagnosis } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { createTranslatedText, dateFormatter } from '@dreryk/react-common';
import {
   IconSize,
   IconWrapper,
   SidePanelListItem,
   useHistoryPushWithBackAction,
   VisitPreviewIcon,
} from '@dreryk/react-components';

import { useVisitStatusGlobalState } from '~legacy.patient';
import { visitPaths } from '~libs.desktop.shared.paths';

import { visitTranslationKeys } from '../../../translations';
import { HistoricalVisitsListItemHeader } from '../../historicalVisits/HistoricalVisitsListItemHeader';

import { DiagnosisCategoryContent } from './DiagnosisCategoryContent';

const EMPTY_VALUE = '-';

export const useDiagnosisSidePanelListItems = (diagnosis: Diagnosis[], patientId: number): SidePanelListItem[] => {
   const push = useHistoryPushWithBackAction(true);
   const { getStatus } = useVisitStatusGlobalState();

   const handleGoToVisitHistory = useCallback(
      (visitId: number | undefined): void => {
         push(
            visitPaths.getVisitHistoryPath({
               params: { patientId: patientId },
               searchParams: { visitId: visitId },
            }),
            { label: visitTranslationKeys.goBackToVisit }
         );
      },
      [patientId, push]
   );

   return useMemo(() => {
      return diagnosis.map((diagnosis) => {
         const visitId = diagnosis.visitId?.asNumber();
         const status = getStatus(visitId);
         const disabled = status !== 'FINISHED_APPROVED';

         return {
            id: diagnosis.id,
            headerContent: (
               <HistoricalVisitsListItemHeader
                  text={createTranslatedText(
                     diagnosis.creationDate ? dateFormatter(diagnosis.creationDate) : EMPTY_VALUE
                  )}
                  icon={
                     <IconWrapper
                        onClick={() => handleGoToVisitHistory(visitId)}
                        testId={'visitHistoryPreview'}
                        size={IconSize.SMALL}
                        disabled={disabled}
                     >
                        <VisitPreviewIcon />
                     </IconWrapper>
                  }
               />
            ),
            content: <DiagnosisCategoryContent diagnosis={diagnosis} />,
         };
      });
   }, [diagnosis, getStatus, handleGoToVisitHistory]);
};
